import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PaperPlaneTilt, CheckCircle, Clock, Envelope, Phone } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { timeAgo } from "../lib/format";

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const [msgs, br] = await Promise.all([api.listTeamMessages({ limit: 200 }), api.listBrands()]);
      setMessages(msgs);
      setBrands(br);
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const list = useMemo(
    () => (filter === "all" ? messages : messages.filter((m) => m.brand === filter)),
    [messages, filter]
  );

  const counts = useMemo(() => {
    const total = messages.length;
    const queued = messages.filter((m) => m.status === "queued_mocked").length;
    const sent = messages.filter((m) => m.status === "sent").length;
    const uniqueBrands = new Set(messages.map((m) => m.brand)).size;
    return { total, queued, sent, uniqueBrands };
  }, [messages]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="messages-page">
      <div className="eyebrow">Internal team · brand outreach</div>
      <h1 className="font-heading font-light text-4xl sm:text-5xl tracking-tight mt-2">
        Direct <span className="font-accent italic text-[#A66E4E]">messages</span> to brands
      </h1>
      <p className="text-[#6E6B65] mt-3 max-w-2xl">
        Every outbound note your team sends to a company after spotting a complaint cluster is
        logged here with full audit trail. Use "Contact brand" on any product page to compose a
        new message.
      </p>

      <section
        className="mt-8 grid grid-cols-2 md:grid-cols-4 grid-borders border border-[#E5E2DC]"
        data-testid="messages-kpi"
      >
        <div className="p-5">
          <div className="eyebrow">Total sent</div>
          <div className="font-heading text-3xl font-light tabular-nums mt-1">{counts.total}</div>
        </div>
        <div className="p-5">
          <div className="eyebrow">Queued</div>
          <div className="font-heading text-3xl font-light tabular-nums mt-1 text-[#F29F05]">
            {counts.queued}
          </div>
        </div>
        <div className="p-5">
          <div className="eyebrow">Delivered</div>
          <div className="font-heading text-3xl font-light tabular-nums mt-1 text-[#40593B]">
            {counts.sent}
          </div>
        </div>
        <div className="p-5">
          <div className="eyebrow">Brands contacted</div>
          <div className="font-heading text-3xl font-light tabular-nums mt-1">{counts.uniqueBrands}</div>
        </div>
      </section>

      <section className="mt-10 border border-[#E5E2DC] bg-white">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-[#E5E2DC]">
          <span className="eyebrow">Filter</span>
          <button
            type="button"
            data-testid="msg-filter-all"
            onClick={() => setFilter("all")}
            className={`px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-semibold border transition-colors ${
              filter === "all"
                ? "bg-[#A66E4E] text-white border-[#A66E4E]"
                : "bg-white text-[#1C1B1A] border-[#E5E2DC] hover:bg-[#EFECE6]"
            }`}
          >
            all brands
          </button>
          <select
            data-testid="msg-brand-filter"
            value={filter === "all" ? "" : filter}
            onChange={(e) => setFilter(e.target.value || "all")}
            className="px-2.5 py-1 text-[11px] tracking-[0.14em] uppercase font-semibold border border-[#E5E2DC] bg-white text-[#1C1B1A]"
          >
            <option value="">— pick brand —</option>
            {brands.map((b) => (
              <option key={b.brand} value={b.brand}>
                {b.brand}
              </option>
            ))}
          </select>
          <span className="ml-auto eyebrow">{list.length} shown</span>
        </div>

        <div className="divide-y divide-[#E5E2DC]">
          {list.length === 0 && (
            <div className="p-10 text-center text-[#6E6B65]">
              No messages yet. Open a product and click <strong>Contact brand</strong> to send one.
            </div>
          )}
          {list.map((m) => (
            <div key={m.id} data-testid="message-row" className="p-5 flex gap-5 hover:bg-[#EFECE6]/30">
              <div className="w-28 shrink-0">
                <div className="eyebrow text-[9px]">to brand</div>
                <div className="font-heading font-medium text-[14px] mt-0.5">{m.brand}</div>
                <div className="text-[10px] font-mono text-[#6E6B65] mt-2">{timeAgo(m.created_at)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  {m.status === "sent" ? (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 bg-[#EDF2EB] text-[#40593B]">
                      <CheckCircle size={10} weight="bold" /> delivered
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5 bg-[#FDF4E6] text-[#F29F05]">
                      <Clock size={10} weight="bold" /> queued
                    </span>
                  )}
                  <span className="flex items-center gap-1 text-[11px] text-[#6E6B65]">
                    <Envelope size={11} /> {m.to_email}
                  </span>
                </div>
                <div className="font-heading text-[16px] leading-tight">{m.subject}</div>
                {m.product_name && (
                  <div className="eyebrow mt-1 text-[9px]">
                    re: <span className="normal-case tracking-normal font-normal text-[#6E6B65]">{m.product_name}</span>
                  </div>
                )}
                <p className="text-[13px] text-[#1C1B1A]/85 leading-snug mt-2 whitespace-pre-line line-clamp-3">
                  {m.body}
                </p>
                <div className="mt-2 text-[11px] text-[#6E6B65]">
                  from <span className="text-[#1C1B1A] font-semibold">{m.sender_name}</span> ·{" "}
                  {m.sender_email}
                  {m.product_id ? (
                    <>
                      {" · "}
                      <Link to={`/product/${m.product_id}`} className="text-[#A66E4E] hover:underline">
                        open product
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
